import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Checkbox,
	ListItemText,
	Typography,
} from '@mui/material';
import { listOfAdministrators } from '../../../api/relevant';
import Select from '../../Select';
import { stores } from '../../../stores';
import { ConfirmDialog } from '../../ConfirmDialog';
import { Button } from '../../Button/Button';
import SubscriptionRecipientTabs from './SubscriptionRecipientTabs';
import { formSubtitleExtras } from './tempStyling';
import { FormOf } from '../../Wrappers';

const availableOccurences = [
	'daily',
	'weekly',
	'monthly',
	'yearly',
];

function SubscriptionForm({ hasDynamicDateRange, onAddSubscription }) {
	const currentUser = stores.identity.user();
	const [users, setUsers] = useState([]);

	// Must have at least one (user/email) recipient
	const formIsValid = (fd, checkErrorsNow) => (fd.model.recipients.length || fd.model.emails.length) && !fd.form.hasErrors(!checkErrorsNow);

	useEffect(() => {
		const getAdmins = async () => {
			const { result } = await listOfAdministrators();
			const admins = result.map((availableUser) => ({
				id: availableUser.id,
				firstName: availableUser.firstname,
				lastName: availableUser.lastname,
				email: availableUser.email,
			}));
			setUsers(admins);
		};
			// Admins can add subscriptions to themselves and other admins
		if (stores.identity.isAdministrator()) {
			getAdmins();
		} else { // Normal users can only add subscriptions to themselves
			setUsers([{
				id: currentUser.id,
				firstName: currentUser.firstname,
				lastName: currentUser.lastname,
				email: currentUser.email,
			}]);
		}
	}, [currentUser.email, currentUser.firstname, currentUser.id, currentUser.lastname]);

	const onSubmit = (fd) => {
		if (!formIsValid(fd, true)) {
			return;
		}
		const { recipients, emails, occurences } = fd.model;
		const resRecipients = recipients.map((id) => {
			const user = users.find(({ id: userId }) => userId === id);
			return {
				userId: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
			};
		});
		const emailOnlyRecipients = emails.map((e) => e.email);
		const newSubscription = {
			occurences: occurences.map((occurence) => ({ type: occurence })),
			addedByUserId: currentUser.id,
		};
		if (resRecipients.length > 0) {
			newSubscription.userRecipients = resRecipients;
		}
		if (emailOnlyRecipients.length > 0) {
			newSubscription.emailOnlyRecipients = emailOnlyRecipients;
		}
		onAddSubscription(newSubscription);
	};

	return (
		<FormOf
			model={{ emails: [], recipients: [], occurences: ['daily'] }}
			content={(fd) => {
				const onSubmitWrap = () => onSubmit(fd);
				return (
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						pt: 3,
						pl: 3,
						pr: 3,
						pb: 4,
						gap: 3,
					}}
					>
						<Box sx={{
							display: 'flex',
							flexDirection: 'column',
							pb: 4,
							gap: 2,
							alignItems: 'flex-start',
							alignSelf: 'stretch',
							borderBottom: 1, borderColor: 'divider',
						}}>
							<Typography variant="h4" {...formSubtitleExtras}>E-mail frequency</Typography>
							<Select
								{...fd.field('occurences')}
								required
								label=""
								hideLabelIfEmpty
								selectProps={{
									multiple: true,
									sx: { minWidth: '132px', textTransform: 'capitalize' },
									renderValue: (selected) => {
										if (selected.length > 1) {
											return `${selected[0]} + ${selected.length - 1}`;
										}
										return selected[0];
									},
								}}
								items={availableOccurences.map((occurence) => ({
									value: occurence,
									label: (
										<>
											<Checkbox checked={fd.model.occurences.indexOf(occurence) !== -1} />
											<ListItemText
												primary={occurence}
												sx={{ textTransform: 'capitalize' }}
											/>
										</>
									),
								}))}
							/>
						</Box>
						<SubscriptionRecipientTabs
							users={users}
							fd={fd}
						/>
						<Box sx={{ ml: 1.5, mt: 2 }}>
							{ !hasDynamicDateRange() ? (
								<ConfirmDialog
									text="Your report has fixed dates,
 						 which means that you will receive the same report in every email.
						 Are you sure you want to continue?"
									onConfirm={onSubmitWrap}
								>
									<Button disabled={!formIsValid(fd)}>
										Add
									</Button>
								</ConfirmDialog>
							) : (
								<Button
									disabled={!formIsValid(fd)}
									onClick={onSubmitWrap}
								>
									Add
								</Button>
							)}
						</Box>
					</Box>
				);
			}}
		/>
	);
}

SubscriptionForm.propTypes = {
	hasDynamicDateRange: PropTypes.func.isRequired,
	onAddSubscription: PropTypes.func.isRequired,
};

export default SubscriptionForm;
